/**
 * Custom Hook für Übersetzungsfunktionalität
 */

import { useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useI18n } from './useI18n';
import { useFirebaseFunctions } from './useFirebaseFunctions';
import { anonymizeText } from '../utils/anonymize';
import { getLanguageLabel, getSwissHint } from '../utils/constants';

export function useTranslation() {
  const { user } = useAuth();
  const { t } = useI18n();
  const { translateEmail, isAvailable: functionsAvailable } = useFirebaseFunctions();
  
  const [inputText, setInputText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [targetLang, setTargetLang] = useState("en");
  const [isTranslating, setIsTranslating] = useState(false);
  const [anonymized, setAnonymized] = useState(false);
  const [error, setError] = useState("");

  const translate = useCallback(async () => {
    if (!inputText.trim()) {
      setError(t.translation.errorEmpty);
      return;
    }

    if (!functionsAvailable) {
      setError(t.common.errorFirebaseUnavailable);
      return;
    }

    if (!user) {
      setError(t.common.errorNotLoggedIn);
      return;
    }

    setIsTranslating(true);
    setError("");
    setTranslatedText("");

    try {
      // Anonymisieren
      const { text: anonymizedText, replaced } = anonymizeText(inputText);
      setAnonymized(replaced);

      // Prompt erstellen
      const langLabel = getLanguageLabel(targetLang);
      const schweizHinweis = getSwissHint(targetLang);
      const prompt = `Übersetze folgenden E-Mail-Text wortgetreu ins ${langLabel}${schweizHinweis}, ohne den Inhalt zu verändern oder zu optimieren:\n\n${anonymizedText}`;

      // Function aufrufen
      const result = await translateEmail({
        model: "gpt-5.6-sol",
        messages: [
          { role: "system", content: "Du bist ein professioneller Übersetzer für E-Mails." },
          { role: "user", content: prompt }
        ],
        maxTokens: 2500,
      }, user);

      if (result.data && result.data.content) {
        setTranslatedText(result.data.content);
      } else {
        setError(t.translation.errorNoResponse);
      }
    } catch (err) {
      setError(t.translation.errorGeneric.replace('{error}', err.message || ''));
    } finally {
      setIsTranslating(false);
    }
  }, [inputText, targetLang, user, functionsAvailable, translateEmail, t]);

  const reset = useCallback(() => {
    setInputText("");
    setTranslatedText("");
    setError("");
    setAnonymized(false);
  }, []);

  return {
    inputText,
    setInputText,
    translatedText,
    targetLang,
    setTargetLang,
    isTranslating,
    anonymized,
    error,
    translate,
    reset,
  };
}
